/**
 * FrostedLightModal — Apple Home 風格毛玻璃 Light 控制面板
 *
 * 設計語言：
 * - Warm Yellow tint → 燈光專用色調
 * - Vertical brightness slider → fill from bottom (brighter = more)
 * - Color temperature presets (2700K → 6500K, 5 levels)
 * - Brightness presets (25% / 50% / 75%)
 * - Dynamic capabilities: only show color temp if device has ColorSetting trait
 *
 * 完全自包含：parent 只需傳 deviceSN + credentials
 *
 * 方向邏輯：
 * - fill 從下往上 = 越亮
 * - brightness 0 = Off (fill 0%), brightness 100 = full (fill 100%)
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import './FrostedLightModal.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const LIGHT_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'slider', label: 'Brightness Slider', icon: 'lightbulb', slots: [
    { id: 'slider-track', label: 'Track', editableProps: ['background', 'borderRadius', 'border'] },
    { id: 'slider-fill', label: 'Fill', editableProps: ['background'] },
    { id: 'slider-percent', label: 'Percentage Text', editableProps: ['fontSize', 'color', 'fontWeight'] },
  ]},
  { id: 'color-temp', label: 'Color Temperature', icon: 'palette', slots: [
    { id: 'color-btn', label: 'Color Button', editableProps: ['width', 'height', 'border'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'presets', label: 'Brightness Presets', icon: 'tune', slots: [
    { id: 'preset-btn', label: 'Preset Button', editableProps: ['background', 'color', 'borderRadius', 'border'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
]

const DEFAULT_SECTION_ORDER = LIGHT_FROSTED_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COLOR_TEMPS = [
  { kelvin: 2700, color: '#ffd699', label: '2700K' },
  { kelvin: 3500, color: '#ffe4b5', label: '3500K' },
  { kelvin: 4000, color: '#fff3d6', label: '4000K' },
  { kelvin: 5000, color: '#e8eeff', label: '5000K' },
  { kelvin: 6500, color: '#ffe0c0', label: '6500K' },
] as const

const BRIGHTNESS_PRESETS = [25, 50, 75] as const

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  if (Array.isArray(v)) v = v[0]
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FrostedLightModal({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
  credentials,
}: DeviceModalTemplateProps) {

  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- Resolve capabilities ----
  const traits = device.traits as string[] | undefined
  const hasTrait = useCallback((t: string) => {
    if (!traits) return false
    return traits.some(tr => tr.toLowerCase() === t.toLowerCase())
  }, [traits])

  const hasColorTemp = hasTrait('ColorSetting') || hasTrait('ColorTemperature') || hasTrait('ColorAbsolute')
  const hasBrightness = hasTrait('Brightness') || hasTrait('BrightnessAbsolute') || true // Most lights have brightness

  // ---- State from device ----
  const isOn = Boolean(getAttr(deviceState, 'on'))
  const currentBrightness = numAttr(deviceState, 'brightness', 'currentBrightness') ?? 0
  const currentColorTemp = numAttr(deviceState, 'colorTemperatureK', 'colorTemp', 'color_temperature') ?? 4000

  // ---- Settings page ----
  const [showSettings, setShowSettings] = useState(false)
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.ip) items.push({ label: 'IP ADDRESS', value: String(device.ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    if (device.rssi != null) items.push({ label: 'RSSI', value: String(device.rssi) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopiedField(label)
    setTimeout(() => setCopiedField(null), 1000)
  }, [])

  // ---- Rename ----
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])

  const startRename = useCallback(() => {
    setRenameValue(displayName)
    setIsRenaming(true)
    setTimeout(() => renameInputRef.current?.focus(), 50)
  }, [displayName])

  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) {
      setIsRenaming(false)
      return
    }
    setDisplayName(trimmed)
    setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  // ---- Local UI state (optimistic) ----
  const [pendingBrightness, setPendingBrightness] = useState<number | null>(null)
  const [pendingColorTemp, setPendingColorTemp] = useState<number | null>(null)
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)

  const displayPower = pendingPower ?? isOn
  const displayBrightness = pendingBrightness ?? currentBrightness
  const displayColorTemp = pendingColorTemp ?? currentColorTemp

  // Sync pending
  useEffect(() => { if (pendingPower !== null && pendingPower === isOn) setPendingPower(null) }, [isOn, pendingPower])
  useEffect(() => { if (pendingBrightness !== null && pendingBrightness === currentBrightness) setPendingBrightness(null) }, [currentBrightness, pendingBrightness])
  useEffect(() => { if (pendingColorTemp !== null && pendingColorTemp === currentColorTemp) setPendingColorTemp(null) }, [currentColorTemp, pendingColorTemp])

  // ---- Internal API caller ----
  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  // ---- Commands ----
  const setBrightness = useCallback((pct: number) => {
    if (isEditorMode) return
    const clamped = Math.max(0, Math.min(100, pct))
    setPendingBrightness(clamped)
    if (clamped === 0) {
      setPendingPower(false)
      execCmd('off')
    } else {
      if (!displayPower) setPendingPower(true)
      execCmd('setBrightness', { brightness: clamped })
    }
  }, [isEditorMode, execCmd, displayPower])

  const setColorTemp = useCallback((kelvin: number) => {
    if (isEditorMode) return
    setPendingColorTemp(kelvin)
    execCmd('ColorAbsolute', { color: { temperature: kelvin } })
  }, [isEditorMode, execCmd])

  // ---- Track drag ----
  const trackRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const dragCommitTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const yToBrightness = useCallback((clientY: number) => {
    const track = trackRef.current
    if (!track) return 0
    const rect = track.getBoundingClientRect()
    // Bottom = 0%, Top = 100%
    return Math.round(Math.max(0, Math.min(100, (1 - (clientY - rect.top) / rect.height) * 100)))
  }, [])

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (isEditorMode) return
    isDraggingRef.current = true
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    setPendingBrightness(yToBrightness(e.clientY))
  }, [isEditorMode, yToBrightness])

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return
    setPendingBrightness(yToBrightness(e.clientY))
  }, [yToBrightness])

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    const pct = yToBrightness(e.clientY)
    if (dragCommitTimer.current) clearTimeout(dragCommitTimer.current)
    dragCommitTimer.current = setTimeout(() => {
      setBrightness(pct)
    }, 100)
  }, [yToBrightness, setBrightness])

  // ---- Derived ----
  const isOff = !displayPower || displayBrightness === 0
  const stateClass = isOff ? 'light-frosted--off' : 'light-frosted--on'
  const statusText = isOff ? 'Off' : `${displayBrightness}%`
  const fillHeight = isOff ? 0 : displayBrightness
  const isFull = displayBrightness >= 100

  // Find closest color temp preset
  const activeColorIdx = useMemo(() => {
    let closest = 0
    let minDiff = Infinity
    COLOR_TEMPS.forEach((ct, i) => {
      const diff = Math.abs(ct.kelvin - displayColorTemp)
      if (diff < minDiff) { minDiff = diff; closest = i }
    })
    return closest
  }, [displayColorTemp])

  const activePreset = BRIGHTNESS_PRESETS.includes(displayBrightness as 25 | 50 | 75) ? displayBrightness : null

  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''

  // ---- Render sections ----
  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null

    switch (sectionId) {
      case 'slider':
        return (
          <div key="slider" className={`light-frosted__slider-area${hl('slider')}`} data-modal-section="slider">
            <div
              className="light-frosted__track"
              ref={trackRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              <div
                className={`light-frosted__fill${isFull ? ' light-frosted__fill--full' : ''}`}
                style={{ height: `${fillHeight}%` }}
              />
              <div className="light-frosted__percent">
                {isOff ? 'Off' : <>{displayBrightness}<span className="light-frosted__percent-unit">%</span></>}
              </div>
              <div className="light-frosted__icon-area">
                <span className="light-frosted__light-icon material-symbols-outlined">
                  {isOff ? 'lightbulb' : 'lightbulb'}
                </span>
              </div>
            </div>
          </div>
        )

      case 'color-temp':
        if (!hasColorTemp) return null
        return (
          <div key="color-temp" className={`light-frosted__color-temps${hl('color-temp')}`} data-modal-section="color-temp">
            {COLOR_TEMPS.map((ct, i) => (
              <button
                key={ct.kelvin}
                className={`light-frosted__color-btn${i === activeColorIdx ? ' active' : ''}`}
                onClick={() => setColorTemp(ct.kelvin)}
                title={ct.label}
              >
                <div className="light-frosted__color-inner" style={{ background: ct.color }} />
              </button>
            ))}
          </div>
        )

      case 'presets':
        return (
          <div key="presets" className={`light-frosted__presets${hl('presets')}`} data-modal-section="presets">
            {BRIGHTNESS_PRESETS.map(p => (
              <button
                key={p}
                className={`light-frosted__preset-btn${activePreset === p ? ' active' : ''}`}
                onClick={() => setBrightness(p)}
              >
                {p}%
              </button>
            ))}
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div
      className={`light-frosted ${stateClass}`}
      data-modal-template-id="light-frosted"
      onContextMenu={handleContextMenu}
    >
      {cssOverrides && <style>{cssOverrides}</style>}

      {/* Header */}
      <div className="light-frosted__header" data-modal-section="_modal">
        <button className="light-frosted__back" onClick={onClose} aria-label="Back">&lsaquo;</button>
        <span className="light-frosted__name">{displayName}</span>
      </div>

      {/* Status */}
      <div className="light-frosted__status">{statusText}</div>

      {/* Ordered sections */}
      {orderedSections.map(id => renderSection(id))}

      {/* Settings button */}
      <button
        className="light-frosted__settings-btn"
        onClick={() => setShowSettings(true)}
        aria-label="Settings"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 28 }}>settings</span>
      </button>

      {/* Settings overlay */}
      {showSettings && (
        <div className="light-frosted__settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="light-frosted__settings-sheet" onClick={e => e.stopPropagation()}>
            <div className="light-frosted__settings-header">
              <span className="light-frosted__settings-title">Settings</span>
              <button className="light-frosted__settings-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>

            <div className="light-frosted__settings-section">
              <div className="light-frosted__settings-section-title">Device Name</div>
              {isRenaming ? (
                <div className="light-frosted__settings-row">
                  <input
                    ref={renameInputRef}
                    className="light-frosted__rename-input"
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                    onBlur={submitRename}
                    maxLength={40}
                  />
                </div>
              ) : (
                <div className="light-frosted__settings-row">
                  <span className="light-frosted__settings-value" style={{ textAlign: 'left', flex: 1 }}>
                    {displayName}
                  </span>
                  <button className="light-frosted__settings-edit" onClick={startRename} title="Rename">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                  </button>
                </div>
              )}
            </div>

            <div className="light-frosted__settings-section">
              <div className="light-frosted__settings-section-title">Device Info</div>
              {deviceInfoItems.map(item => (
                <div key={item.label} className="light-frosted__settings-row">
                  <span className="light-frosted__settings-label">{item.label}</span>
                  <span className="light-frosted__settings-value">{item.value}</span>
                  {item.action === 'copy' && (
                    <button
                      className="light-frosted__settings-copy"
                      onClick={() => copyToClipboard(item.value, item.label)}
                      title="Copy"
                    >
                      📋
                      {copiedField === item.label && (
                        <span className="light-frosted__copied-toast">Copied</span>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
